import { apiClient } from '@/lib/api-client';
import type { Product, ProductVariantsResponse } from '@bbpos/types';

/** What a product's stock must carry — mirrors the API's TrackingMode. */
export type TrackingMode = 'NONE' | 'BATCH' | 'SERIAL';

/** Units in unexpired batches and the expired batches behind them, at one store (QA IS4-004). */
export interface ExpiredBatchStock {
  unexpired_quantity: number;
  expired_batches: { batch_number: string; expiry_date: string; quantity: number }[];
}

export interface SerialLookupResult {
  ok: boolean;
  status?: string;
  serial_number?: string;
  /** Cashier-facing reason when `ok` is false. */
  message?: string;
}

export interface StockHistoryEntry {
  id: string;
  type: string;
  quantity: number;
  unit_cost: number;
  reference_id?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
}

export interface ProductListFilters {
  /** 'pos' = counter billing / restaurant ordering, 'sales' = quotations. Drops products the owner hid for that channel. */
  channel?: 'pos' | 'sales';
  search?: string;
  category?: string;
  location_id?: string;
  is_active?: string;
  stock_status?: string;
  page?: number;
  limit?: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─── Quick-Add types (POST /products/quick-add) ─────────────────────────────
//
// Mirror of services/api/src/products/dto/product.dto.ts → QuickAddProductDto.
// All currency fields are paise (BIGINT). Stock as decimal quantity.

export interface QuickAddProductPayload {
  barcode: string;
  location_id: string;
  /** G2.8: optional SKU. Backend falls back to `QA-${last8}` when omitted. */
  sku?: string;
  name?: string;
  /** C03: the picked category master row (the API refuses an unknown or inactive one). */
  category_id?: string;
  /** Selling price in paise */
  price: number;
  /** Cost price in paise */
  cost?: number;
  /** GST rate: 0 | 5 | 12 | 18 | 28 */
  gst_rate: number;
  hsn_code?: string;
  stock_quantity?: number;
  /** YYYY-MM-DD — accepted by API but not yet persisted */
  expiry_date?: string;
  image_url?: string;
}

// ─── Barcode lookup (POST /products/lookup) ─────────────────────────────────
//
// Two-stage lookup: local DB first, then external barcode database
// (Open Food Facts) for product suggestions when local has no match.

export type BarcodeLookupSource = 'local' | 'external' | 'none';

export interface BarcodeLookupSuggestion {
  barcode: string;
  name?: string;
  brand?: string;
  category?: string;
  image_url?: string;
  description?: string;
}

export interface BarcodeLookupResult {
  source: BarcodeLookupSource;
  product: Product | null;
  suggestions: BarcodeLookupSuggestion | null;
}

// ─── Image-based product lookup (POST /products/lookup/by-image) ────────────
//
// G2.13 — visual search escape hatch for unlabeled produce, bulk grains,
// bakery, and packaged goods without a working barcode. The cashier captures
// a still frame from the live camera scanner; the backend computes a
// perceptual hash and returns ranked candidates. Mirrors the return shape of
// services/api/src/products/image-lookup.service.ts.

export interface ImageLookupCandidate {
  product: {
    id: string;
    name: string;
    sku: string;
    /** Selling price in paise */
    price: number;
    image_url: string | null;
    stock_quantity: number;
  };
  /** 0..64, lower is closer */
  distance: number;
  /** 0..1, higher is more confident */
  confidence: number;
}

export interface ImageLookupResult {
  source: 'local' | 'none';
  /** 16-char hex of the query image's perceptual hash */
  query_hash: string;
  candidates: ImageLookupCandidate[];
}


// ─── Recipe availability (GET /products/availability) ───────────────────────
//
// #308 — RESTAURANT vertical: max preparable portions per menu item from
// current ingredient stock. Products without a recipe are omitted (treated as
// not recipe-limited). RETAIL tenants get an empty list.

export interface ProductAvailability {
  product_id: string;
  available: number;
  limiting_ingredient: string;
}

// ─── HSN/SAC type-ahead ──────────────────────────────────────────────────────

export interface HsnCodeSuggestion {
  code: string;
  description: string;
  gst_rate: number;
  is_service: boolean;
}

// ─── Stock adjustment (POST /products/:id/stock) ────────────────────────────
//
// SALE and RETURN are system-generated by the billing flow. The Quick-Add page
// uses ADJUSTMENT (default), DAMAGE (wastage toggle), or PURCHASE.

// G2.8: WASTAGE + EXPIRY_WRITE_OFF added per FR-PROD-003 / PAGES.md §2.29.
// Matches the backend `StockAdjustmentDto.type` enum.
export type StockAdjustmentType =
  | 'PURCHASE'
  | 'ADJUSTMENT'
  | 'DAMAGE'
  | 'OPENING_STOCK'
  | 'WASTAGE'
  | 'EXPIRY_WRITE_OFF';

export interface StockAdjustmentPayload {
  type: StockAdjustmentType;
  /** Positive integer — direction is encoded in `type` */
  quantity: number;
  /** Cost per unit in paise (required for PURCHASE) */
  unit_cost?: number;
  notes?: string;
}

export const productsService = {
  list(filters: ProductListFilters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    return apiClient<{ data: Product[]; meta: PaginationMeta }>(
      `/products${qs ? `?${qs}` : ''}`,
    );
  },

  /**
   * Active variants of a parent product for the POS picker — the server
   * returns the attribute groups (library-ordered) and the child rows.
   */
  getVariants(parentProductId: string, channel?: 'pos' | 'sales') {
    const qs = channel ? `?channel=${channel}` : '';
    return apiClient<ProductVariantsResponse>(
      `/products/${encodeURIComponent(parentProductId)}/variants${qs}`,
    );
  },

  getCategories(locationId?: string) {
    const params = locationId ? `?location_id=${locationId}` : '';
    return apiClient<string[]>(`/products/categories${params}`);
  },

  getById(productId: string) {
    return apiClient<Product>(`/products/${productId}`);
  },

  /**
   * Recipe-based availability for the given products (#308). Restaurant-only
   * in practice — the backend returns [] for verticals with product-level
   * stock. Lets the billing page block add-to-cart on ingredient shortages
   * instead of failing at payment.
   */
  getAvailability(productIds: string[]) {
    return apiClient<ProductAvailability[]>(
      `/products/availability?ids=${productIds.map(encodeURIComponent).join(',')}`,
    );
  },

  getByBarcode(barcode: string, locationId?: string, channel?: 'pos' | 'sales') {
    const params = new URLSearchParams();
    if (locationId) params.set('location_id', locationId);
    if (channel) params.set('channel', channel);
    const qs = params.toString();
    return apiClient<Product>(`/products/barcode/${encodeURIComponent(barcode)}${qs ? `?${qs}` : ''}`);
  },

  /**
   * Two-stage barcode lookup — checks local DB then external suggestion API.
   * Used by Quick-Add to auto-fill product details when scanning a new barcode.
   * Rate-limited to 10 external lookups/min/tenant by the backend.
   */
  lookupBarcode(barcode: string, locationId?: string) {
    return apiClient<BarcodeLookupResult>('/products/lookup', {
      method: 'POST',
      body: { barcode, location_id: locationId },
    });
  },

  /**
   * Image-based product lookup. Captures a frame from the live camera scanner
   * and POSTs it as multipart/form-data. Returns up to 5 ranked candidates;
   * the cashier confirms which one to add to the cart.
   *
   * Backend rate-limits to 10 req/min/tenant. The endpoint is CASHIER+.
   */
  lookupByImage(imageBlob: Blob, locationId?: string) {
    const form = new FormData();
    // The backend's FileInterceptor expects field "image" with a filename
    // hint (some Multer setups reject blobs without one).
    form.append('image', imageBlob, 'capture.jpg');
    if (locationId) {
      form.append('location_id', locationId);
    }
    return apiClient<ImageLookupResult>('/products/lookup/by-image', {
      method: 'POST',
      body: form,
      formData: true,
    });
  },

  /**
   * Quick-add a product via barcode scan. Used by the Quick-Add Product page.
   * MANAGER+ only. Sends a minimal product payload — full edit happens later
   * in the catalog. `expiry_date` and `image_url` are accepted but not yet
   * persisted server-side (per current QuickAddProductDto).
   */
  quickAdd(payload: QuickAddProductPayload) {
    return apiClient<Product>('/products/quick-add', {
      method: 'POST',
      body: payload,
    });
  },

  /**
   * Adjust stock on an existing product.
   * Used by the Quick-Add Product page when the scanned barcode matches an
   * existing product. MANAGER+ only.
   */
  adjustStock(productId: string, payload: StockAdjustmentPayload) {
    return apiClient<Product>(`/products/${encodeURIComponent(productId)}/stock`, {
      method: 'POST',
      body: payload,
    });
  },

  getStockHistory(
    productId: string,
    page = 1,
    limit = 20,
  ) {
    return apiClient<{ data: StockHistoryEntry[]; meta: PaginationMeta }>(
      `/products/${productId}/stock-history?page=${page}&limit=${limit}`,
    );
  },

  hsnSearch(query: string, limit = 10) {
    return apiClient<HsnCodeSuggestion[]>(
      `/products/hsn/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    );
  },

  /**
   * Effective tracking mode per product — which cart lines must capture serial
   * numbers before checkout. Resolves per-product overrides against the tenant
   * setting server-side.
   */
  getTrackingModes(productIds: string[]) {
    if (productIds.length === 0) return Promise.resolve({} as Record<string, TrackingMode>);
    return apiClient<Record<string, TrackingMode>>(
      `/products/tracking-modes?product_ids=${encodeURIComponent(productIds.join(','))}`,
    );
  },

  /**
   * Expired batches a sale at `locationId` would take, per product (QA IS4-004).
   * Only products holding an expired batch appear; a sale uses the unexpired
   * units first, then the expired batches soonest expiry first.
   */
  getExpiredBatchStock(productIds: string[], locationId: string) {
    if (productIds.length === 0) return Promise.resolve({} as Record<string, ExpiredBatchStock>);
    return apiClient<Record<string, ExpiredBatchStock>>(
      `/products/expired-batch-stock?product_ids=${encodeURIComponent(productIds.join(','))}` +
        `&location_id=${encodeURIComponent(locationId)}`,
    );
  },

  /**
   * Validate one scanned serial against a product before adding it to the
   * line. A fast pre-check for the counter — completion re-validates inside
   * its own transaction, so this never reserves the unit.
   */
  lookupSerial(productId: string, serial: string, locationId?: string) {
    const q = new URLSearchParams({ serial });
    if (locationId) q.set('location_id', locationId);
    return apiClient<SerialLookupResult>(
      `/products/${encodeURIComponent(productId)}/serials/lookup?${q.toString()}`,
    );
  },
};
